/**
 * Global Object Render Fix
 * 
 * This utility provides a global solution to prevent React error #31
 * by intercepting and safely handling objects with {value, unit} structure
 * before they can cause rendering errors.
 */

import { safeRenderValue } from './safeRender';

/**
 * Recursively processes React children to safely handle objects
 * @param {any} children - React children to process
 * @returns {any} - Safely processed children
 */
const processReactChildren = (children) => {
  if (children === null || children === undefined) {
    return children;
  }

  // Handle arrays of children
  if (Array.isArray(children)) {
    return children.map(child => processReactChildren(child));
  }

  // Handle objects that might be {value, unit} structures
  if (typeof children === 'object' && children !== null) {
    // Check if it's a React element (has $$typeof property)
    if (children.$$typeof) {
      return children; // It's a React element, leave it alone
    }

    // Check if it's an object with {value, unit} structure
    if (children.hasOwnProperty('value') && children.hasOwnProperty('unit')) {
      console.warn('Global Object Render Fix: Converting {value, unit} object to safe string:', children);
      return safeRenderValue(children);
    }

    // Check if it's any other object that shouldn't be rendered
    if (children.constructor === Object) {
      console.warn('Global Object Render Fix: Converting plain object to safe string:', children);
      return safeRenderValue(children);
    }

    return children;
  }

  return children;
};

/**
 * Monkey patch React.createElement to intercept and fix object rendering
 */
const applyGlobalObjectRenderFix = () => {
  // Only apply in development mode to avoid production issues
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  const React = require('react');
  const originalCreateElement = React.createElement;

  React.createElement = function(type, props, ...children) {
    try {
      // Process children to handle potential objects
      const safeChildren = children.map(child => processReactChildren(child));
      
      // Process props that might contain objects
      let safeProps = props;
      if (props) {
        safeProps = { ...props };
        
        // Check common props that might contain objects
        const propsToCheck = ['children', 'value', 'defaultValue', 'title', 'description'];
        propsToCheck.forEach(propName => {
          if (safeProps[propName] !== undefined) {
            safeProps[propName] = processReactChildren(safeProps[propName]);
          }
        });
      }

      return originalCreateElement.call(this, type, safeProps, ...safeChildren);
    } catch (error) {
      console.error('Global Object Render Fix: Error in createElement patch:', error);
      // Fallback to original function
      return originalCreateElement.call(this, type, props, ...children);
    }
  };

  console.log('Global Object Render Fix: Applied React.createElement monkey patch');
};

/**
 * Alternative approach: Monkey patch common Ant Design components
 */
const applyAntDesignComponentFixes = () => {
  try {
    // Skip Ant Design patches for now as they're causing issues
    console.log('Global Object Render Fix: Skipping Ant Design component patches to avoid conflicts');
    return;

    /* Disabled for now - causing module.exports issues
    const { Typography, Statistic, Tag } = require('antd');

    // Patch Typography.Text
    if (Typography && Typography.Text) {
      const OriginalText = Typography.Text;
      Typography.Text = function(props) {
        const safeProps = { ...props };
        if (safeProps.children !== undefined) {
          safeProps.children = processReactChildren(safeProps.children);
        }
        return OriginalText.call(this, safeProps);
      };
    }

    console.log('Global Object Render Fix: Applied Ant Design component patches');
    */
  } catch (error) {
    console.error('Global Object Render Fix: Error applying Ant Design patches:', error);
  }
};

/**
 * Global error handler for React error #31
 */
const applyGlobalErrorHandler = () => {
  const originalConsoleError = console.error;

  console.error = function(...args) {
    const errorMessage = args[0];

    // Check if this is React error #31 (more comprehensive check)
    if (typeof errorMessage === 'string' &&
        (errorMessage.includes('Objects are not valid as a React child') ||
         errorMessage.includes('object with keys') ||
         errorMessage.includes('Minified React error #31'))) {

      console.warn('🚨 Global Object Render Fix: Intercepted React error #31!');
      console.warn('📍 Error Details:', ...args);

      // Try to get stack trace information
      const error = new Error('React Error #31 Stack Trace');
      console.warn('📍 Stack Trace:', error.stack);

      // Log additional debugging information
      console.warn('💡 This error was caused by trying to render an object as React children.');
      console.warn('💡 The object likely has a {value, unit} structure that needs to be handled.');
      console.warn('💡 Check the stack trace above to identify the component causing the issue.');

      // Still log the error but with additional context
      originalConsoleError.call(this, '🔍 INTERCEPTED React Error #31:', ...args);

      // Don't return - let the error continue to be processed
    }

    // For all other errors, use original console.error
    originalConsoleError.call(this, ...args);
  };
};

/**
 * Initialize all global fixes
 */
export const initializeGlobalObjectRenderFix = () => {
  console.log('Global Object Render Fix: Initializing...');
  
  try {
    applyGlobalObjectRenderFix();
    applyAntDesignComponentFixes();
    applyGlobalErrorHandler();
    
    console.log('Global Object Render Fix: Successfully initialized all fixes');
  } catch (error) {
    console.error('Global Object Render Fix: Failed to initialize:', error);
  }
};

export default {
  initializeGlobalObjectRenderFix,
  processReactChildren,
  applyGlobalObjectRenderFix,
  applyAntDesignComponentFixes,
  applyGlobalErrorHandler
};
